import React, { useState, useEffect } from "react";
import { Eye, Edit, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { Modal, SearchBar, Pagination } from "@/components/ui";
import { fmt } from "@/lib/formatters";
import { PurchaseForm } from "../../components/PurchaseForm";
import { useClientPagination } from "@/hooks";

import { useInventorySettings } from "../hooks/use_inventory_settings";
import { Table } from "../components/table_view";
import { VendorForm } from "../components/vendor_form";
import { ItemForm } from "../components/item_form";
import { StoreForm } from "../components/store_form";
import { CategoryForm } from "../components/category_form";
import { ViewModal } from "../components/view_modal";

export const InventorySettingsScreen = () => {
  const {
    activeTab, setActiveTab,
    search, setSearch,
    isModalOpen, setIsModalOpen,
    editingItem, setEditingItem,
    viewingItem, setViewingItem,
    loading,
    gstRegisteredTypes,
    stores,
    vendors,
    categories,
    brands,
    units,
    errors,
    isPurchaseFormOpen, setIsPurchaseFormOpen,
    user,
    form, setForm,
    TABS,
    fetchSettings,
    handleAdd,
    handleEdit,
    handleSave,
    handleInputChange,
    handleDelete,
    getFilteredData
  } = useInventorySettings();

  const [page, setPage] = useState(1);
  const filteredData = getFilteredData();
  const { paginatedData, totalPages } = useClientPagination(filteredData, page, 10);

  // Reset page to 1 when tab or search changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, search]);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between border-b border-slate-200 overflow-x-auto no-scrollbar whitespace-nowrap">
        <div className="flex">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setActiveTab(t.id);
                setSearch("");
              }}
              className={`px-6 py-4 text-sm font-semibold border-b-2 transition-all ${
                activeTab === t.id
                  ? "border-[#C91D25] text-[#C91D25]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleAdd}
          className="bg-[#0f172a] hover:bg-[#1e293b] text-white flex items-center justify-center gap-2 py-2 px-5 rounded-sm text-sm font-semibold active:scale-95 transition-all ml-4 shrink-0"
        >
          <span>
            +{" "}
            {(() => {
              const label = TABS.find((t) => t.id === activeTab)?.label || "";
              return label.endsWith("ies")
                ? label.replace(/ies$/, "y")
                : label.replace(/s$/, "");
            })()}
          </span>
        </button>
      </div>

      <div>
        <div className="py-2 border-b border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={`Search by title or code...`}
            className="w-full sm:max-w-md"
            inputClassName="rounded-sm"
          />
        </div>

        <div className="overflow-x-auto min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-brand-blue border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : activeTab === "supplier" ? (
            <Table
              headers={[
                "Code",
                "Name",
                "Contact",
                "Address",
                "Created At",
                "Status",
                "Actions",
              ]}
            >
              {paginatedData.map((s) => (
                <tr
                  key={s._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {s.code || s.vendorCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {s.name || s.vendorName}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {s.mobile}
                  </td>
                  <td
                    className="px-4 py-1.5 text-[12px] text-slate-500 font-medium truncate max-w-[200px] text-left"
                    title={s.address || s.addressLine1}
                  >
                    {s.address || s.addressLine1 || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(s.createdAt || s.createdDate || s.created)
                      ? new Date(s.createdAt || s.createdDate || s.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    {s.active !== false ? (
                      <span className="inline-flex gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-400 border border-slate-200">
                        <XCircle size={10} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setViewingItem(s)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(s)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(s._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : activeTab === "registeredType" ? (
            <Table
              headers={[
                "Code",
                "Name",
                "Description",
                "Created At",
                "Status",
                "Actions",
              ]}
            >
              {paginatedData.map((item) => (
                <tr
                  key={item._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {item.code || item.registeredTypeCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {item.title || item.registeredTypeName || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] text-slate-600 font-medium text-left">
                    {item.description || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(item.createdAt || item.createdDate || item.created)
                      ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    {(item.active !== false && item.status !== "INACTIVE") ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-400 border border-slate-200">
                        <XCircle size={10} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : activeTab === "purchase" ? (
            <Table
              headers={[
                "Bill No",
                "Vendor",
                "Total Amount",
                "Bill Date",
                "Created At",
                "Actions",
              ]}
            >
              {paginatedData.map((p) => (
                <tr
                  key={p._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {p.billNo || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {typeof p.vendor === "object"
                      ? p.vendor?.name || p.vendor?.title
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-bold text-emerald-600 text-left">
                    {fmt(p.netPayable || 0)}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {p.date ? new Date(p.date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(p.createdAt || p.createdDate || p.created)
                      ? new Date(p.createdAt || p.createdDate || p.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(p)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(p)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(p._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : activeTab === "item" ? (
            <Table
              headers={[
                "Code",
                "Name",
                "Category",
                "Brand",
                "Units",
                "Fixing Type",
                "MOQ",
                "Created At",
                "Actions",
              ]}
            >
              {paginatedData.map((item) => (
                <tr
                  key={item._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {item.itemCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {item.itemName}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {item.category?.categoryName || item.category?.title || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {item.brand?.brandName || item.brand?.title || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {item.units?.unitName || item.units?.title || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {item.fixingType || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {item.moq || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(item.createdAt || item.createdDate || item.created)
                      ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : activeTab === "stores" ? (
            <Table
              headers={[
                "Code",
                "Name",
                "Address",
                "Contact",
                "Created At",
                "Status",
                "Actions",
              ]}
            >
              {paginatedData.map((store) => (
                <tr
                  key={store._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {store.code || store.storeCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {store.title || store.storeName}
                  </td>
                  <td
                    className="px-4 py-1.5 text-[12px] text-slate-500 font-medium truncate max-w-[200px] text-left"
                    title={store.address || store.addressLine1}
                  >
                    {store.address || store.addressLine1 || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] font-semibold text-slate-600 text-left">
                    {store.mobile || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(store.createdAt || store.createdDate || store.created)
                      ? new Date(store.createdAt || store.createdDate || store.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    {store.active !== false ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-400 border border-slate-200">
                        <XCircle size={10} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(store)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(store)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(store._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : (
            <Table
              headers={[
                "Code",
                "Title",
                "Description",
                "Created At",
                "Status",
                "Actions",
              ]}
            >
              {paginatedData.map((item) => (
                <tr
                  key={item._id}
                  className="hover:bg-slate-50 transition-colors align-top h-[34px]"
                >
                  <td className="px-4 py-1.5 text-[12px] font-mono font-bold text-brand-blue text-left">
                    {item.code || item.unitCode || item.brandCode || item.categoryCode || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[13px] font-semibold text-brand-dark text-left">
                    {item.title || item.unitName || item.brandName || item.categoryName || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[12px] text-slate-600 font-medium text-left">
                    {item.description || "—"}
                  </td>
                  <td className="px-4 py-1.5 text-[11px] font-mono font-bold text-slate-400 text-left">
                    {(item.createdAt || item.createdDate || item.created)
                      ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    {item.active !== false ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-400 border border-slate-200">
                        <XCircle size={10} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setViewingItem(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}

          {!loading && filteredData.length > 0 && (
            <div className="py-4 border-t border-slate-100 flex justify-end">
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`${editingItem ? "Edit" : "Add"} ${
          activeTab === "registeredType"
            ? "Registered Type"
            : activeTab === "gstRegisteredType"
            ? "GST Registered Type"
            : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
        }`}
      >
        <div className="space-y-6">
          {activeTab === "supplier" ? (
            <VendorForm
              form={form}
              setForm={setForm}
              errors={errors}
              handleInputChange={handleInputChange}
              gstRegisteredTypes={gstRegisteredTypes}
            />
          ) : activeTab === "item" ? (
            <ItemForm
              form={form}
              setForm={setForm}
              errors={errors}
              handleInputChange={handleInputChange}
              units={units}
              categories={categories}
              brands={brands}
            />
          ) : activeTab === "stores" ? (
            <StoreForm
              form={form}
              setForm={setForm}
              errors={errors}
              handleInputChange={handleInputChange}
            />
          ) : (
            <CategoryForm
              form={form}
              setForm={setForm}
              errors={errors}
              handleInputChange={handleInputChange}
              activeTab={activeTab}
            />
          )}

          <div className="flex flex-col sm:flex-row-reverse gap-3 pt-6 border-t border-slate-100">
            <button
              onClick={handleSave}
              className="btn-primary flex-1 py-3 font-bold shadow-lg shadow-blue-900/20 transition-all active:scale-[0.98] hover:translate-y-[-1px]"
            >
              Save Changes
            </button>
            <button
              onClick={() => setIsModalOpen(false)}
              className="btn-secondary flex-1 py-3 font-semibold transition-all hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <ViewModal
        viewingItem={viewingItem}
        setViewingItem={setViewingItem}
        activeTab={activeTab}
        handleEdit={handleEdit}
      />

      {/* Purchase Form Modal */}
      <PurchaseForm
        isOpen={isPurchaseFormOpen}
        onClose={() => setIsPurchaseFormOpen(false)}
        user={user}
        onPurchaseSuccess={fetchSettings}
        initialData={editingItem}
        dynamicSettings={{
          stores: stores,
          suppliers: vendors,
        }}
      />
    </div>
  );
};
